import { NAV_CATALOG, type NavCatalogEntry, type NavKind } from './nav-prefs-catalog';

export interface NavMatch {
  entry: NavCatalogEntry;
  kind: NavKind;
}

function stripQueryHash(p: string): string {
  return p.split('?')[0]!.split('#')[0]!;
}

function pathOnly(p: string): string {
  const stripped = stripQueryHash(p).replace(/\/+$/, '');
  return stripped === '' ? '/' : stripped;
}

function getQuery(p: string): string {
  const idx = p.indexOf('?');
  return idx === -1 ? '' : p.slice(idx + 1).split('#')[0]!;
}

/** Resolve a navigated-to URL to a catalog entry + kind.
 *  Rules (in order):
 *   1. Exact match on `createPath` → create.
 *   2. ?new=1 query against an entry path → create (covers contact pages
 *      that don't have a dedicated /new route).
 *   3. Exact match on `entry.path` → list.
 *   4. Path starts with `entry.path + '/'` and entry has a detail route
 *      → detail. Tail "new" / "new/..." is excluded (handled by rule 1).
 *   5. Tie-break: longest matching `entry.path` wins (so
 *      /storefront/orders/123 doesn't match /storefront).
 */
export function matchNavTarget(rawPath: string): NavMatch | null {
  const path = pathOnly(rawPath);
  const query = getQuery(rawPath);
  const isQueryCreate = /(^|&)new=1(&|$)/.test(query);

  let best: NavMatch | null = null;

  for (const entry of NAV_CATALOG) {
    if (entry.createPath && path === pathOnly(entry.createPath)) {
      return { entry, kind: 'create' };
    }

    if (path === entry.path) {
      // For pages with a `?new=1` create-flow (no createPath defined),
      // recognise the query and emit a create match.
      if (isQueryCreate && !entry.createPath && entry.kinds.includes('create')) {
        return { entry, kind: 'create' };
      }
      if (!best || entry.path.length > best.entry.path.length) {
        best = { entry, kind: 'list' };
      }
      continue;
    }

    if (entry.hasDetail && path.startsWith(entry.path + '/')) {
      const tail = path.slice(entry.path.length + 1);
      if (tail === 'new' || tail.startsWith('new/')) continue;
      if (!best || entry.path.length > best.entry.path.length) {
        best = { entry, kind: 'detail' };
      }
    }
  }

  return best;
}
